/**
 * trailStyles — how a movement trail is drawn, by style id.
 *
 * A style is a pure draw function over the sample ring from TrailHistory; it
 * knows nothing about players or the network. Adding a new look is one
 * `registerTrailStyle()` call; which look a player gets comes from their
 * `cosmetics.equippedTrail` resolved against config/cosmetics.json `trails`
 * (the `default` entry is used when nothing is equipped), so trails are a
 * cosmetic slot from day one, alongside skins.
 *
 * Style contract:
 *   draw(g, samples, ctx)
 *     g       — a PIXI.Graphics (already cleared) in world space
 *     samples — [{x, y, t}] oldest → newest, newest is at the player
 *     ctx     — { now, lifeMs, color (0xRRGGBB), secondaryColor, width, alpha, isLocal }
 *
 * Every built-in fades with age (t → now) so the tail always thins out, and
 * draws nothing for fewer than 2 samples.
 */
const registry = new Map();

export function registerTrailStyle(id, style) {
  if (!id || typeof style?.draw !== 'function') throw new Error(`trail style ${id} needs a draw()`);
  registry.set(id, style);
}

export function getTrailStyle(id) {
  return registry.get(id) || null;
}

export function trailStyleIds() {
  return [...registry.keys()];
}

/** '#rrggbb' | 0xrrggbb | undefined → 0xrrggbb (fallback white). */
export function toColor(c, fallback = 0xffffff) {
  if (typeof c === 'number') return c;
  if (typeof c === 'string' && /^#?[0-9a-f]{6}$/i.test(c)) return parseInt(c.replace('#', ''), 16);
  return fallback;
}

/**
 * Which trail definition applies to a player: their equipped trail if it exists
 * in config, else the config's default entry, else a built-in ribbon.
 * @returns {{ id, style, color, secondaryColor, width, lifeMs, alpha }}
 */
export function resolveTrailDefinition(player, cosmeticsConfig) {
  const trails = cosmeticsConfig?.trails || [];
  const wanted = player?.cosmetics?.equippedTrail;
  const def = (wanted && trails.find((t) => t.id === wanted))
    || trails.find((t) => t.isDefault)
    || { id: 'default', style: 'ribbon', color: '#ffffff' };
  return {
    id: def.id,
    style: registry.has(def.style) ? def.style : 'ribbon',
    color: def.color,
    secondaryColor: def.secondaryColor,
    width: def.width ?? 14,
    lifeMs: def.lifeMs ?? 500,
    alpha: def.alpha ?? 0.55
  };
}

/** 0 (oldest, expired) → 1 (just sampled). */
function freshness(sample, now, lifeMs) {
  return Math.max(0, Math.min(1, 1 - (now - sample.t) / lifeMs));
}

// ───────────────────────── built-in styles ─────────────────────────

// Ribbon: a tapered stroke, wide and solid at the player, thin and faint at the tail.
registerTrailStyle('ribbon', {
  draw(g, samples, { now, lifeMs, color, width, alpha }) {
    for (let i = 1; i < samples.length; i++) {
      const a = samples[i - 1];
      const b = samples[i];
      const f = freshness(b, now, lifeMs);
      if (f <= 0) continue;
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
      g.stroke({ width: Math.max(1, width * f), color, alpha: alpha * f, cap: 'round', join: 'round' });
    }
  }
});

// Dots: a disc at every sample, shrinking and fading with age (reads as "footprints").
registerTrailStyle('dots', {
  draw(g, samples, { now, lifeMs, color, secondaryColor, width, alpha }) {
    for (let i = 0; i < samples.length - 1; i++) {
      const s = samples[i];
      const f = freshness(s, now, lifeMs);
      if (f <= 0) continue;
      g.circle(s.x, s.y, Math.max(1, (width / 2) * f));
      g.fill({ color: i % 2 && secondaryColor != null ? secondaryColor : color, alpha: alpha * f });
    }
  }
});

// Dashes: every other segment, like tyre marks; good for a "fast" feel.
registerTrailStyle('dashes', {
  draw(g, samples, { now, lifeMs, color, width, alpha }) {
    for (let i = 1; i < samples.length; i += 2) {
      const a = samples[i - 1];
      const b = samples[i];
      const f = freshness(b, now, lifeMs);
      if (f <= 0) continue;
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
      g.stroke({ width: Math.max(1, width * 0.6 * f), color, alpha: alpha * f, cap: 'butt' });
    }
  }
});
